export const API_URL = import.meta.env.VITE_API_URL || '/api';
import {
  PricingPackage,
  CreatePackageDTO,
  UpdatePackageDTO,
  Coupon,
  CreateCouponDTO,
  UpdateCouponDTO,
  Bundle,
  CreateBundleDTO,
  AuditLogEntry,
} from '@/types/pricing';
import { generateIdempotencyKey } from '@/utils/idempotency';
interface LoginData {
  email: string;
  password: string;
  turnstileToken?: string;
}

interface RegisterData {
  name: string;
  email: string;
  password: string;
  turnstileToken?: string;
}

interface AuthResponse {
  success: boolean;
  message?: string;
  data?: {
    user: {
      id: string;
      name: string;
      email: string;
      role: 'user' | 'admin';
      subscription?: any;
      requirePasswordChange?: boolean;
    };
    token: string;
  };
}

// Store token in localStorage
export const setAuthToken = (token: string) => {
  localStorage.setItem('authToken', token);
};

export const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

export const removeAuthToken = () => {
  localStorage.removeItem('authToken');
};

// Store user data
export const setUserData = (user: any) => {
  localStorage.setItem('userData', JSON.stringify(user));
};

export const getUserData = () => {
  const data = localStorage.getItem('userData');
  return data ? JSON.parse(data) : null;
};

export const removeUserData = () => {
  localStorage.removeItem('userData');
};

// API call with auth token
const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const token = getAuthToken();
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  // Automatically add idempotency key for mutations
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method?.toUpperCase() || '')) {
    headers['Idempotency-Key'] = generateIdempotencyKey();
  }
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    if (response.status === 401) {
      const isPublicAuthRequest = [
        '/auth/login',
        '/auth/register',
        '/auth/forgot-password',
        '/auth/reset-password',
        '/auth/activate-account',
        '/auth/logout',
      ].some((path) => endpoint.startsWith(path));

      if (!isPublicAuthRequest && typeof window !== 'undefined') {
        removeAuthToken();
        removeUserData();
        window.location.replace('/auth');
      }
    }

    const error = new Error(data.message || 'API request failed') as any;

    error.code = data.code;
    error.status = response.status;
    error.data = data.data;

    throw error;
  }

  return data;
};

// Auth API calls
export const authAPI = {
  register: async (data: RegisterData): Promise<AuthResponse> => {
    const response = await apiCall('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    // Don't store token or fetch user data after registration
    // The user needs to verify their email first
    // Token and user data will be stored after email verification

    return response;
  },

  login: async (data: LoginData): Promise<AuthResponse> => {
    const response = await apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (response.success && response.data) {
      setAuthToken(response.data.token);

      // fetch full user
      const me = await apiCall('/auth/me');

      if (me.success && me.data) {
        setUserData(me.data);
      }
    }

    return response;
  },

  logout: async () => {
    try {
      await apiCall('/auth/logout', {
        method: 'POST',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      removeAuthToken();
      removeUserData();
    }
  },

  getMe: async () => {
    const response = await apiCall('/auth/me');
    if (response.success && response.data) {
      setUserData(response.data);
    }
    return response;
  },

  forgotPassword: async (email: string) => {
    return await apiCall('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    return await apiCall('/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  resetPassword: async (token: string, password: string) => {
    return await apiCall(`/auth/reset-password/${token}`, {
      method: 'PUT',
      body: JSON.stringify({ password }),
    });
  },

  activateAccount: async (token: string, password: string) => {
    return await apiCall(`/auth/activate-account/${token}`, {
      method: 'PUT',
      body: JSON.stringify({ password }),
    });
  },
};

// Strengths API calls
interface StrengthRank {
  strengthName: string;
  rank: number;
}

interface SaveStrengthsData {
  username: string;
  strengths: StrengthRank[];
}

interface StrengthsResponse {
  success: boolean;
  message?: string;
  data?: any;
}

export const strengthsAPI = {
  saveRanks: async (data: SaveStrengthsData): Promise<StrengthsResponse> => {
    return await apiCall('/strengths/ranks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getUserStrengths: async (userId?: string): Promise<StrengthsResponse> => {
    const endpoint = userId ? `/strengths/user/${userId}` : '/strengths/me';
    return await apiCall(endpoint);
  },

  getAllStrengths: async (): Promise<StrengthsResponse> => {
    return await apiCall('/strengths');
  },

  // Admin: Get all users strengths overview
  getAllUsersOverview: async (): Promise<StrengthsResponse> => {
    return await apiCall('/strengths/ranks/all/overview');
  },

  // Get individual user strength ranks with optional view filter
  getUserRanks: async (
    username: string,
    view?: 'top10' | 'bydomain'
  ): Promise<StrengthsResponse> => {
    const queryParam = view ? `?view=${view}` : '';
    return await apiCall(`/strengths/ranks/${username}${queryParam}`);
  },

  // Get current user's top 10 strengths
  getMyTop10: async (): Promise<StrengthsResponse> => {
    return await apiCall('/users/strengths/rankings/top10');
  },

  // Get current user's strengths by domain
  getMyByDomain: async (): Promise<StrengthsResponse> => {
    return await apiCall('/users/strengths/rankings/domains');
  },

  // Admin: Bulk update email for all strength ranks of a user
  bulkUpdateUserEmail: async (
    username: string,
    email: string
  ): Promise<StrengthsResponse> => {
    return await apiCall(`/strengths/ranks/bulk/${username}`, {
      method: 'PUT',
      body: JSON.stringify({ email }),
    });
  },
};

export const usersAPI = {
  getDirectory: async (page: number = 1, limit: number = 12) => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });

    return await apiCall(`/users/directory?${params.toString()}`);
  },

  getDirectoryUserProfile: async (userId: string) => {
    return await apiCall(`/users/directory/${userId}/profile`);
  },

  // Get public profile (NO AUTH)
  getPublicProfile: async (userId: string) => {
    return await apiCall(`/users/${userId}/public-profile`, {
      method: 'GET',
    });
  },

  // Toggle profile visibility (AUTH REQUIRED)
  updateProfileVisibility: async (isProfilePublic: boolean) => {
    return await apiCall('/users/profile-visibility', {
      method: 'PATCH',
      body: JSON.stringify({ isProfilePublic }),
    });
  },

  // Update profile (AUTH REQUIRED)
  updateProfile: async (profileData: any) => {
    return await apiCall('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  },

  // Get current user's Clifton Strengths from their latest successful report
  getMyCliftonStrengths: async () => {
    return await apiCall('/users/me/clifton-strengths');
  },
};

export interface ThemeSummary {
  id: string;
  name: string;
  nameAr?: string | null;
  domain: string;
  domainColor?: string | null;
}

export interface ThemeLibraryItem extends ThemeSummary {
  description?: string | null;
  descriptionAr?: string | null;
  shortDescription?: string | null;
  order?: number;
}

export interface ThemeLeaderboardEntry {
  userId: string;
  name: string;
  category?: "student" | "graduate";
  profileImage?: string | null;
  profileImageDisplayUrl?: string | null;
  rank: number;
  score?: number | null;
  topDomains?: { domain: string; count: number }[];
}

export interface TeamStrengthsMapTheme extends ThemeSummary {
  count: number;
  avg: number | null;
  profileCount: number;
}

export interface TeamStrengthsMapMember extends ThemeLeaderboardEntry {
  isTop10: boolean;
}

export interface TeamStrengthsMapOverviewResponse {
  success: boolean;
  data: {
    category: "student" | "graduate" | "all";
    memberCount: number;
    rankedProfileCount: number;
    maxCount: number;
    themes: TeamStrengthsMapTheme[];
  };
}

export interface TeamStrengthsMapThemeResponse {
  success: boolean;
  data: {
    theme: ThemeSummary;
    category: "student" | "graduate" | "all";
    members: TeamStrengthsMapMember[];
  };
}

export interface ThemeLeaderboardResponse {
  success: boolean;
  data: {
    theme: ThemeSummary;
    category: "student" | "graduate" | "all";
    leaderboard: ThemeLeaderboardEntry[];
  };
}

export const themesAPI = {
  // Get the Theme Library: all 34 CliftonStrengths themes with domain + description
  getThemes: async (): Promise<{ success: boolean; count: number; data: ThemeLibraryItem[] }> => {
    return await apiCall('/themes');
  },

  // Get the leaderboard ranking of a single theme.
  // Regular users are always scoped to their own category (category param is ignored server-side).
  // Admins may pass an explicit category ("student" | "graduate"), or omit it to view all categories.
  getThemeLeaderboard: async (
    themeId: string,
    category?: "student" | "graduate"
  ): Promise<ThemeLeaderboardResponse> => {
    const params = category ? `?category=${category}` : '';
    return await apiCall(`/themes/${themeId}/leaderboard${params}`);
  },

  getTeamStrengthsMap: async (
    category?: "student" | "graduate"
  ): Promise<TeamStrengthsMapOverviewResponse> => {
    const params = category ? `?category=${category}` : '';
    return await apiCall(`/themes/team-map${params}`);
  },

  getTeamStrengthsMapTheme: async (
    themeId: string,
    category?: "student" | "graduate"
  ): Promise<TeamStrengthsMapThemeResponse> => {
    const params = category ? `?category=${category}` : '';
    return await apiCall(`/themes/team-map/${themeId}${params}`);
  },
};

// Content API calls
interface ContentResponse {
  success: boolean;
  message?: string;
  data?: any;
  count?: number;
}

interface GenerateContentRequest {
  strength: string;
  domain: string;
}

interface GenerateContentResponse {
  success: boolean;
  data: {
    strength: string;
    domain: string;
    shortDescription: string;
    mainContent: string;
  };
}

interface QuizQuestion {
  question: string;
  options: string[];
  correct_answer: string;
  explanation: string;
}

interface GenerateQuizRequest {
  strength: string;
  short_description: string;
}

interface GenerateQuizResponse {
  success: boolean;
  data: {
    strength: string;
    questions: QuizQuestion[];
  };
}

export const contentAPI = {
  // Get all contents with optional filters
  getAllContents: async (status?: 'draft' | 'published', search?: string): Promise<ContentResponse> => {
    let endpoint = '/content';
    const params = new URLSearchParams();
    
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    
    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }
    
    return await apiCall(endpoint);
  },

  // Get content by ID
  getContentById: async (id: string): Promise<ContentResponse> => {
    return await apiCall(`/content/${id}`);
  },

  // Get content by strength ID
  getContentByStrengthId: async (strengthId: string): Promise<ContentResponse> => {
    return await apiCall(`/content/strength/${strengthId}`);
  },

  // Create content (multipart/form-data)
  createContent: async (formData: FormData): Promise<ContentResponse> => {
    const token = getAuthToken();
    
    const response = await fetch(`${API_URL}/content`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to create content');
    }

    return data;
  },

  // Update content (multipart/form-data)
  updateContent: async (id: string, formData: FormData): Promise<ContentResponse> => {
    const token = getAuthToken();
    
    const response = await fetch(`${API_URL}/content/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to update content');
    }

    return data;
  },

  // Delete content
  deleteContent: async (id: string): Promise<ContentResponse> => {
    return await apiCall(`/content/${id}`, {
      method: 'DELETE',
    });
  },

  // Toggle publish/unpublish
  togglePublish: async (id: string): Promise<ContentResponse> => {
    return await apiCall(`/content/${id}/publish`, {
      method: 'PATCH',
    });
  },

  // Get content statistics
  getStats: async (): Promise<ContentResponse> => {
    return await apiCall('/content/stats/overview');
  },

  // Generate content with AI
  generateContent: async (data: GenerateContentRequest): Promise<GenerateContentResponse> => {
    return await apiCall('/content/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Generate quiz with AI
  generateQuiz: async (data: GenerateQuizRequest): Promise<GenerateQuizResponse> => {
    return await apiCall('/content/generate-quiz', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// PDF Extraction API calls
export const pdfAPI = {
  // Get PDF extraction history with pagination
  getHistory: async (page: number = 1, limit: number = 5, targetUserId?: string | null): Promise<any> => {
    const skip = (page - 1) * limit;
    const params = new URLSearchParams({
      limit: String(limit),
      skip: String(skip),
    });

    if (targetUserId) {
      params.append('targetUserId', targetUserId);
    }

    return await apiCall(`/extract/history?${params.toString()}`);
  },

  // Get a single PDF extraction report
  getReport: async (id: string): Promise<any> => {
    return await apiCall(`/extract/history/${id}`);
  },

  // Delete extraction report
  deleteReport: async (id: string): Promise<any> => {
    return await apiCall(`/extract/history/${id}`, {
      method: 'DELETE',
    });
  },
};

// Teams API calls
interface TeamResponse {
  success: boolean;
  message?: string;
  data?: any;
  alreadyMember?: boolean;
  code?: string;
}

interface CreateTeamData {
  name: string;
  description?: string;
}

export const teamsAPI = {
  // Create a new team
  createTeam: async (data: CreateTeamData): Promise<TeamResponse> => {
    return await apiCall('/teams', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Get all teams for the current user
  getMyTeams: async (): Promise<TeamResponse> => {
    return await apiCall('/teams');
  },

  // Get team by ID
  getTeamById: async (teamId: string): Promise<TeamResponse> => {
    return await apiCall(`/teams/${teamId}`);
  },

  // Update team
  updateTeam: async (teamId: string, data: Partial<CreateTeamData>): Promise<TeamResponse> => {
    return await apiCall(`/teams/${teamId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Delete team
  deleteTeam: async (teamId: string): Promise<TeamResponse> => {
    return await apiCall(`/teams/${teamId}`, {
      method: 'DELETE',
    });
  },

  // Add member to team
  addMember: async (teamId: string, memberData: { email: string; name: string; role: 'member' | 'admin'; reason?: string }): Promise<TeamResponse> => {
    return await apiCall(`/teams/${teamId}/members`, {
      method: 'POST',
      body: JSON.stringify(memberData),
    });
  },

  // Get team members
  getTeamMembers: async (teamId: string): Promise<TeamResponse> => {
    return await apiCall(`/teams/${teamId}/members`);
  },

  // Remove member from team
  removeMember: async (teamId: string, memberId: string): Promise<TeamResponse> => {
    return await apiCall(`/teams/${teamId}/members/${memberId}`, {
      method: 'DELETE',
    });
  },

  // Update member role
  updateMemberRole: async (teamId: string, memberId: string, role: 'member' | 'admin'): Promise<TeamResponse> => {
    return await apiCall(`/teams/${teamId}/members/${memberId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  },

  // Get team comparison data
  getTeamComparison: async (teamId: string): Promise<TeamResponse> => {
    return await apiCall(`/teams/${teamId}/comparison`);
  },

  // generate link to join team
  generateInviteLink: async (teamId: string): Promise<TeamResponse> => {
    return await apiCall(`/teams/${teamId}/invite-link`, {
      method: 'POST',
    });
  },

  // join the team 
  joinWithInvite: async (token: string): Promise<TeamResponse> => {
    return await apiCall(`/teams/invites/join`, {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  //  Validate invite token
  validateInvite: async (token: string) => {
    return await apiCall(`/teams/invite/${token}`, {
      method: 'GET',
    });
  },

  //  Request new invite
  requestNewInvite: async (token: string) => {
    return await apiCall(`/teams/invite/request-new`, {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },  

};

// Documents API calls
interface DocumentResponse {
  success: boolean;
  message?: string;
  data?: any;
  count?: number;
}

export const documentsAPI = {
  // Generate a new document
  generateDocument: async (data: {
    title: string;
    domain_name: string;
    description?: string;
  }): Promise<DocumentResponse> => {
    return await apiCall('/documents/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Get all documents with pagination
  getDocuments: async (limit: number = 50, skip: number = 0): Promise<DocumentResponse> => {
    return await apiCall(`/documents?limit=${limit}&skip=${skip}`);
  },

  // Download a document
  downloadDocument: async (fileName: string): Promise<Blob> => {
    const token = getAuthToken();
    
    const response = await fetch(`${API_URL}/documents/download/${fileName}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/pdf',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to download document');
    }

    return await response.blob();
  },
};

// RAG System API
export interface RAGUploadResponse {
  success: boolean;
  message: string;
  filesProcessed: number;
  reset: boolean;
  details?: any;
}

export interface RAGDocument {
  filename: string;
  original_filename: string;
  file_type: string;
  size_mb: number;
  uploaded_at: string;
  path: string;
}

export interface RAGDocumentsResponse {
  success: boolean;
  documents: RAGDocument[];
  count: number;
}

export interface RAGChatResponse {
  success: boolean;
  answer: string;
  sources?: Array<{
    document: string;
    content: string;
    score: number;
  }>;
  metadata?: {
    resultsReturned: number;
    processingTime: number;
  };
}

export const ragAPI = {
  // Get all uploaded documents (Admin only)
  getDocuments: async (): Promise<RAGDocumentsResponse> => {
    return await apiCall('/rag/documents', {
      method: 'GET',
    });
  },

  // Upload documents (Admin only)
  uploadDocuments: async (files: File[], reset: boolean = false): Promise<RAGUploadResponse> => {
    const token = getAuthToken();
    const formData = new FormData();
    
    files.forEach(file => {
      formData.append('files', file);
    });

    const url = reset 
      ? `${API_URL}/rag/upload?reset=true` 
      : `${API_URL}/rag/upload`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to upload documents');
    }

    return await response.json();
  },

  // Chat with RAG system (Admin and User)
  chat: async (
    question: string,
    nResults: number = 5,
    includeSources: boolean = true
  ): Promise<RAGChatResponse> => {
    return await apiCall('/rag/chat', {
      method: 'POST',
      body: JSON.stringify({
        question,
        n_results: nResults,
        include_sources: includeSources,
      }),
    });
  },
};

// Plans API
export interface PlanSection {
  heading: string;
  content: string[];
}

export interface AIGeneratedContent {
  duration: string;
  expertise: string;
  language: string;
  sections: PlanSection[];
  strengths: string[];
  success: boolean;
  title: string;
  topics: string[];
}

export interface Plan {
  _id: string;
  userId: string;
  title: string;
  duration: number;
  status: 'active' | 'completed' | 'draft';
  goals: string[];
  overallProgress: number;
  generatedAt: string;
  aiGeneratedContent: AIGeneratedContent;
  createdAt: string;
  updatedAt: string;
}

export interface PlansResponse {
  success: boolean;
  count: number;
  data: Plan[];
}

export const plansAPI = {
  // Get all plans for the authenticated user
  getAllPlans: async (): Promise<PlansResponse> => {
    return await apiCall('/plans', {
      method: 'GET',
    });
  },

  // Get a specific plan by ID
  getPlanById: async (planId: string): Promise<{ success: boolean; data: Plan }> => {
    return await apiCall(`/plans/${planId}`, {
      method: 'GET',
    });
  },

  // Generate a new plan with AI
  generatePlan: async (data: { 
    language: string; 
    expertise: string;
  }): Promise<{ success: boolean; data: Plan }> => {
    return await apiCall('/plans/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

interface AnalyticsData {
  totalUsers: number;
  activeUsers: number;
  completionRate: number;
  avgProgress: number;
  growthRate: number;
  popularStrengths: Array<{
    name: string;
    nameAr: string;
    domain: string;
    userCount: number;
    avgRank: number;
  }>;
}

interface AnalyticsResponse {
  success: boolean;
  data: AnalyticsData;
  message?: string;
}

export interface AdminUser {
  _id: string;
  name: string;
  email: string;
  role: 'user' | 'admin';
  category: UserCategory;
  isActive: boolean;
  profileImage?: string;
  profileImageDisplayUrl?: string;
  createdAt?: string;
  lastLogin?: string;
}

export type UserCategory = 'student' | 'graduate';

export interface AdminUsersResponse {
  success: boolean;
  count: number;
  total: number;
  totalPages: number;
  currentPage: number;
  data: AdminUser[];
}

export interface AdminUserPayload {
  name: string;
  email: string;
  role: 'user' | 'admin';
  category: UserCategory;
  status: 'active' | 'inactive';
}

export interface AdminUserResponse {
  success: boolean;
  message?: string;
  data: AdminUser;
  profileImageUrl?: string;
  previousImageDeleted?: boolean;
}

const adminFormDataRequest = async <T>(endpoint: string, formData: FormData): Promise<T> => {
  const token = getAuthToken();
  const headers: HeadersInit = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'POST',
    headers,
    body: formData,
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.message || data.error || 'API request failed') as any;
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
};

export const adminAPI = {
  // Get admin analytics
  getAnalytics: async (): Promise<AnalyticsResponse> => {
    return await apiCall('/admin/analytics', {
      method: 'GET',
    });
  },

  getUsers: async (params: { search?: string; role?: string; status?: string; page?: number; limit?: number } = {}): Promise<AdminUsersResponse> => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        query.append(key, String(value));
      }
    });

    const suffix = query.toString() ? `?${query.toString()}` : '';
    return await apiCall(`/admin/users${suffix}`);
  },

  createUser: async (data: AdminUserPayload): Promise<AdminUserResponse> => {
    return await apiCall('/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateUser: async (id: string, data: AdminUserPayload): Promise<AdminUserResponse> => {
    return await apiCall(`/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  updateUserStatus: async (id: string, status: 'active' | 'inactive'): Promise<AdminUserResponse> => {
    return await apiCall(`/admin/users/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },

  uploadUserProfileImage: async (id: string, file: File): Promise<AdminUserResponse> => {
    const formData = new FormData();
    formData.append('profileImage', file);
    return await adminFormDataRequest(`/admin/users/${id}/profile-image`, formData);
  },

  deleteUser: async (id: string): Promise<{ success: boolean; message?: string }> => {
    return await apiCall(`/admin/users/${id}`, {
      method: 'DELETE',
    });
  },
};


// Notifications API
interface NotificationResponse {
  success?: boolean;
  data?: any;
  unreadCount?: number;
}

export const notificationsAPI = {
  // Get unread notifications
  getNotifications: async (): Promise<any> => {
    return await apiCall('/notifications');
  },

  // Get unread count
  getUnreadCount: async (): Promise<{ unreadCount: number }> => {
    return await apiCall('/notifications/unread-count');
  },

  // Mark all as read
  markAllAsRead: async (): Promise<{ success: boolean }> => {
    return await apiCall('/notifications/read-all', {
      method: 'PUT',
    });
  },
};
export const pricingAPI = {
  // Packages
  getPackages: async (search?: string, status?: string) => {
    let endpoint = '/admin/pricing/packages';
    const params = new URLSearchParams();
    if (search) params.append('q', search);
    if (status) params.append('status', status);
    if (params.toString()) endpoint += `?${params.toString()}`;
    return await apiCall(endpoint);
  },

  getPublicPackages: async () => {
    return await apiCall('/packages');
  },

  schedulePriceOverride: async (data: { entity_type: string, entity_id: string, amount: number, currency: string, effective_from: string, effective_until?: string }) => {
    return await apiCall('/admin/pricing/prices', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  deletePriceOverride: async (entityType: string, entityId: any) => {
    return await apiCall(`/admin/pricing/prices/${entityType}/${entityId}`, {
      method: 'DELETE',
    });
  },

  getPackageById: async (id: any) => {
    return await apiCall(`/admin/pricing/packages/${id}`);
  },

  createPackage: async (data: CreatePackageDTO) => {
    return await apiCall('/admin/pricing/packages', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updatePackage: async (id: any, data: UpdatePackageDTO) => {
    return await apiCall(`/admin/pricing/packages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deactivatePackage: async (id: any) => {
    return await apiCall(`/admin/pricing/packages/${id}/deactivate`, {
      method: 'PATCH',
    });
  },

  deletePackage: async (id: any) => {
    return await apiCall(`/admin/pricing/packages/${id}`, {
      method: 'DELETE',
    });
  },

  // Coupons
  getCoupons: async (search?: string) => {
    let endpoint = '/admin/pricing/coupons';
    if (search) endpoint += `?q=${search}`;
    return await apiCall(endpoint);
  },

  createCoupon: async (data: CreateCouponDTO) => {
    return await apiCall('/admin/pricing/coupons', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateCoupon: async (id: any, data: UpdateCouponDTO) => {
    return await apiCall(`/admin/pricing/coupons/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deactivateCoupon: async (id: any) => {
    return await apiCall(`/admin/pricing/coupons/${id}/deactivate`, {
      method: 'PATCH',
    });
  },

  activateCoupon: async (id: any) => {
    return await apiCall(`/admin/pricing/coupons/${id}/activate`, {
      method: 'PATCH',
    });
  },

  getCouponAnalytics: async (id: any) => {
    return await apiCall(`/admin/pricing/coupons/${id}/analytics`);
  },

  // Bundles
  getBundles: async () => {
    return await apiCall('/admin/pricing/bundles');
  },

  createBundle: async (data: CreateBundleDTO) => {
    return await apiCall('/admin/pricing/bundles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateBundle: async (id: any, data: Partial<CreateBundleDTO>) => {
    return await apiCall(`/admin/pricing/bundles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteBundle: async (id: any) => {
    return await apiCall(`/admin/pricing/bundles/${id}`, {
      method: 'DELETE',
    });
  },

  // Audit Logs
  getAuditLogs: async (params: any = {}) => {
    let endpoint = '/admin/pricing/audit';
    const query = new URLSearchParams(params);
    if (query.toString()) endpoint += `?${query.toString()}`;
    return await apiCall(endpoint);
  },
};
